import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import { Box, Card, CardContent, Chip, Divider, Stack, Typography } from "@mui/material";
import take from "lodash/take";
import type { FC } from "react";
import dayjs from "../../dayjs";
import type { ECommerceOfferResponse, ListECommerceOffersResponseItem } from "../../eCommerce/eCommerceApi";
import { useFilteredECommerceOffers } from "../../eCommerce/useFilteredECommerceOffers";
import { CardMediaImg } from "../../publicPackages2/CardImage";
import { CardLinkArea } from "../../publicPackages2/CardLinkArea";
import { packageCountries } from "../../publicPackages2/packageTagsInfo";
import { TransportationName } from "../../publicPackages2/transportation";
interface Props {
  readonly mainOffer: ECommerceOfferResponse;
}

const deduplicateOffers = (offers: ListECommerceOffersResponseItem[]): ListECommerceOffersResponseItem[] => {
  const seenPackageIds = new Set<string>();
  return offers.filter((offer) => {
    if (!offer.packageId) {
      return true;
    }
    if (seenPackageIds.has(offer.packageId)) {
      return false;
    }
    seenPackageIds.add(offer.packageId);
    return true;
  });
};

const OtherOffers: FC<Props> = ({ mainOffer }) => {
  const { offers: filteredOffers, isLoading } = useFilteredECommerceOffers({
    month: dayjs(mainOffer.start),
    text: "",
    tags: [],
  });

  const offers = deduplicateOffers(filteredOffers);

  if (isLoading || offers.length === 0) {
    return null;
  }
  return (
    <Box sx={{ mt: 8, pt: 5, borderTop: "1px solid rgba(0,0,0,0.08)" }}>
      <Typography variant="h5" sx={{ mb: 4, fontWeight: "bold" }}>
        Sprawdź też podobne gotowe wycieczki
      </Typography>

      <Box
        sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: 4 }}
      >
        {take(offers, 5).map((offer) => {
          const countries = packageCountries(offer);
          return (
            <Box key={offer.id.value} sx={{ width: "100%" }}>
              <Card
                elevation={0}
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  borderRadius: 1,
                  border: "1px solid rgba(0,0,0,0.05)",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 28px rgba(0,0,0,0.08)",
                  },
                }}
              >
                <CardLinkArea
                  to={`../${offer.id.value}`}
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  sx={{ flexGrow: 1, display: "flex", flexDirection: "column", alignItems: "stretch" }}
                >
                  <Box sx={{ position: "relative" }}>
                    <CardMediaImg height="160" imageUrl={offer.image?.thumbnail.url} alt={offer.name} />
                    <Chip
                      label={`ID: ${offer.id.userFriendly}`}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        bgcolor: "rgba(255,255,255,0.9)",
                        fontWeight: "bold",
                      }}
                    />
                  </Box>

                  <CardContent sx={{ flexGrow: 1, p: 2.5, display: "flex", flexDirection: "column" }}>
                    <Typography variant="subtitle1" sx={{ lineHeight: 1.3, mb: 1, fontWeight: "bold" }}>
                      {offer.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 2, flexGrow: 1 }}>
                      {offer.info}
                    </Typography>

                    <Stack direction="row" sx={{ mt: "auto", mb: 2, flexWrap: "wrap", gap: 0.5 }}>
                      {countries?.slice(0, 2).map((c) => (
                        <Chip
                          key={c}
                          label={c}
                          size="small"
                          sx={{
                            fontSize: "0.65rem",
                            height: 20,
                            bgcolor: "rgba(13, 110, 253, 0.08)",
                            color: "primary.main",
                            fontWeight: 500,
                          }}
                        />
                      ))}
                      {countries?.length > 2 && (
                        <Chip
                          label={`+${countries.length - 2}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "0.65rem", height: 20 }}
                        />
                      )}
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    <Stack
                      direction="row"
                      sx={{ alignItems: "center", justifyContent: "space-between", color: "text.secondary" }}
                    >
                      <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                        <AccessTimeIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                          {offer.days} dni
                        </Typography>
                      </Stack>
                      <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                        {<TransportationName forPackage={offer} />}
                      </Typography>
                      <Stack direction="row" sx={{ alignItems: "center" }} spacing={0.5}>
                        <PersonIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                          {offer.participantsNumber.numberOfPaid}
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </CardLinkArea>
              </Card>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default OtherOffers;
